import type { IconName } from '@customerio/pluma-icons';
import type { KeyboardShortcutProps } from '@customerio/pluma-components/react';
import type { ActionType, TemplateType, CampaignType } from '@/services/auth/types';

export const LABEL = 'Search for anything';

export const DEFERRED_SOURCE_FALLBACK_DELAY_MS = 1500;
export const COMMAND_MODE_HOTKEY = '>' as KeyboardShortcutProps['hotkey'];
export const FILTER_MODE_HOTKEY = '/' as KeyboardShortcutProps['hotkey'];

export const COMMAND_DIALOG_QUERY_DEFAULTS = {
  staleTime: 1000 * 30,
  retryOnMount: false,
} as const;

export const SHARED_QUERY_OPTIONS = {
  ...COMMAND_DIALOG_QUERY_DEFAULTS,
  forwardAbortSignal: true,
} as const;

export const CLIENT_SIDE_RESULT_CAP = 50;

/** Kind filter values used by the SegmentedControl when `universal-search-multi` is enabled. */
export const SEARCH_KIND_FILTERS = {
  segments: 'segment',
  campaigns: 'campaign',
  actions: 'action',
  templates: 'template',
  newsletters: 'newsletter',
  goals: 'goal',
  transactional_messages: 'transactional_message',
  anonymous_in_apps: 'anonymous_in_app',
  snippets: 'snippet',
  email_layouts: 'email_layout',
  collections: 'collection',
  navigation: 'navigation',
} as const;

export type SearchKindFilter = (typeof SEARCH_KIND_FILTERS)[keyof typeof SEARCH_KIND_FILTERS];

export type ActiveCategory = 'all' | 'people' | 'docs' | 'integrations' | 'forms' | 'objects' | SearchKindFilter;

const SEARCH_KIND_FILTER_VALUES = Object.values(SEARCH_KIND_FILTERS) as SearchKindFilter[];
const DEFAULT_ACTIVE_CATEGORIES = [
  'all',
  'people',
  'docs',
  'integrations',
  'forms',
  'objects',
  ...Object.values(SEARCH_KIND_FILTERS),
] as const satisfies readonly ActiveCategory[];

export function isActiveCategory(
  value: string | null | undefined,
  categories: readonly ActiveCategory[] = DEFAULT_ACTIVE_CATEGORIES,
): value is ActiveCategory {
  return value != null && (categories as readonly string[]).includes(value);
}

export function isSearchKindFilter(value: ActiveCategory): value is SearchKindFilter {
  return (SEARCH_KIND_FILTER_VALUES as readonly ActiveCategory[]).includes(value);
}

type CategorySearchCopy = {
  placeholder: string;
  ariaLabel: string;
};

const CATEGORY_SEARCH_COPY = {
  all: {
    placeholder: LABEL,
    ariaLabel: 'Search this workspace for resources, pages, and commands.',
  },
  segment: {
    placeholder: 'Search for segments',
    ariaLabel: 'Search this workspace for segments.',
  },
  campaign: {
    placeholder: 'Search for campaigns',
    ariaLabel: 'Search this workspace for campaigns.',
  },
  action: {
    placeholder: 'Search for campaign actions (email, delay, branch…)',
    ariaLabel: 'Search this workspace for campaign actions, like email, delay, or branch.',
  },
  template: {
    placeholder: 'Search for templates',
    ariaLabel: 'Search this workspace for templates.',
  },
  newsletter: {
    placeholder: 'Search for newsletters',
    ariaLabel: 'Search this workspace for newsletters.',
  },
  // `newsletter` copy above is the flag-off default; getCategorySearchCopy overrides it
  // with the resolved newsletters/one-time sends taxonomy term.
  goal: {
    placeholder: 'Search for goals',
    ariaLabel: 'Search this workspace for goals.',
  },
  transactional_message: {
    placeholder: 'Search for transactional messages',
    ariaLabel: 'Search this workspace for transactional messages.',
  },
  anonymous_in_app: {
    placeholder: 'Search for anonymous in-app messages',
    ariaLabel: 'Search this workspace for anonymous in-app messages.',
  },
  snippet: {
    placeholder: 'Search for snippets',
    ariaLabel: 'Search this workspace for snippets.',
  },
  email_layout: {
    placeholder: 'Search for email layouts',
    ariaLabel: 'Search this workspace for email layouts.',
  },
  collection: {
    placeholder: 'Search for collections',
    ariaLabel: 'Search this workspace for collections.',
  },
  navigation: {
    placeholder: 'Search for pages',
    ariaLabel: 'Search for pages and settings in this workspace.',
  },
  people: {
    placeholder: 'Search for people',
    ariaLabel: 'Search this workspace for people.',
  },
  // `people` copy above is the flag-off default; getCategorySearchCopy overrides it
  // with the resolved people/profiles taxonomy term.
  docs: {
    placeholder: 'Search the docs',
    ariaLabel: 'Search Customer.io documentation.',
  },
  integrations: {
    placeholder: 'Search for integrations',
    ariaLabel: 'Search this workspace for integrations.',
  },
  forms: {
    placeholder: 'Search for forms',
    ariaLabel: 'Search this workspace for forms.',
  },
  objects: {
    placeholder: 'Search for object types',
    ariaLabel: 'Search this workspace for object types.',
  },
} as const satisfies Record<ActiveCategory, CategorySearchCopy>;

export function getCategorySearchCopy(
  activeCategory: ActiveCategory,
  peoplePlural: string,
  newsletterPlural: string,
): CategorySearchCopy {
  if (activeCategory === 'people') {
    return {
      placeholder: `Search for ${peoplePlural}`,
      ariaLabel: `Search this workspace for ${peoplePlural}.`,
    };
  }
  if (activeCategory === 'newsletter') {
    return {
      placeholder: `Search for ${newsletterPlural}`,
      ariaLabel: `Search this workspace for ${newsletterPlural}.`,
    };
  }
  return CATEGORY_SEARCH_COPY[activeCategory];
}

export const FACEBOOK_LEAD_ADS_FORM_TYPE = 'facebook_lead_ads';

export const CUSTOM_FORM_TYPE_LABELS: Record<string, string> = {
  custom: 'Custom',
  typeform: 'Typeform',
  jotform: 'Jotform',
  in_app_form: 'In-App Form',
  [FACEBOOK_LEAD_ADS_FORM_TYPE]: 'Facebook Lead Ads',
};

/** Form types that use brand SVG images instead of a Pluma icon. Filenames under /hydra-assets/icons/. */
export const CUSTOM_FORM_TYPE_IMAGE_FILENAMES: Record<string, string> = {
  [FACEBOOK_LEAD_ADS_FORM_TYPE]: 'facebook.svg',
  typeform: 'typeform.svg',
  jotform: 'jotform.svg',
};

/** Form types that map to a Pluma icon name. Falls back to 'form' for unknown types. */
export const CUSTOM_FORM_TYPE_PLUMA_ICONS: Partial<Record<string, IconName>> = {
  custom: 'form',
  in_app_form: 'in-app',
};

export const ACTION_TYPE_ICONS = {
  conditional_wait_action: 'wait',
  delay_seconds_action: 'time-delay',
  delay_time_window_action: 'time-window',
  grace_period_action: 'time-delay',
  email_action: 'email',
  slack_action: 'slack',
  twilio_action: 'sms',
  line_action: 'sms',
  push_action: 'mobile',
  urban_airship_action: 'mobile',
  in_app_action: 'in-app',
  webhook_action: 'webhook',
  attribute_update_action: 'person',
  static_seg_update_action: 'segment-manual',
  collection_query_action: 'collection',
  create_event_action: 'event',
  batch_update_action: 'person',
  split_randomized_action: 'a-b-test',
  conditional_branch_action: 'branch-two',
  multi_split_branch_action: 'branch-three',
  multi_lang_branch_action: 'localization',
  random_cohort_branch_action: 'random-cohort',
  exit_action: 'exit',
  filter_match_delay_action: 'time-delay',
  randomized_delay_action: 'time-delay',
  trigger: 'trigger-event',
  llm_action: 'sparkle',
  inbox_action: 'notifications',
  whatsapp_action: 'sms',
} as const satisfies Record<ActionType, IconName>;

export const TEMPLATE_TYPE_ICONS = {
  email: 'email',
  webhook: 'webhook',
  twilio: 'sms',
  urban_airship: 'mobile',
  slack: 'slack',
  push: 'mobile',
  create_event: 'trigger-event',
  attribute_update: 'person',
  batch_update_not_configured: 'person',
  batch_create_event: 'person',
  batch_attribute_update: 'person',
  in_app: 'in-app',
  in_app_broadcast: 'in-app',
  llm_action_prompt: 'sparkle',
  llm_action_result_schema: 'sparkle',
  line: 'sms',
  inbox: 'notifications',
  whatsapp: 'sms',
  unset: 'newsletter',
} as const satisfies Record<TemplateType, IconName>;

export const TEMPLATE_TYPE_LABELS = {
  email: 'Email',
  webhook: 'Webhook',
  twilio: 'SMS',
  urban_airship: 'Airship',
  slack: 'Slack Message',
  push: 'Push Notification',
  line: 'LINE Message',
  inbox: 'Inbox Message',
  whatsapp: 'WhatsApp',
  in_app: 'In-App Message',
  in_app_broadcast: 'In-App Message',
  create_event: 'Send Event',
  attribute_update: 'Create or Update Person',
  batch_update_not_configured: 'Batch Update',
  batch_create_event: 'Batch Update',
  batch_attribute_update: 'Batch Update',
  llm_action_prompt: 'Run LLM',
  llm_action_result_schema: 'Run LLM',
  unset: 'Newsletter',
} as const satisfies Record<TemplateType, string>;

export const TEMPLATE_TYPE_FILTER_OPTIONS = [
  { label: TEMPLATE_TYPE_LABELS.email, value: 'email' },
  { label: TEMPLATE_TYPE_LABELS.push, value: 'push' },
  { label: TEMPLATE_TYPE_LABELS.in_app, value: 'in_app' },
  { label: TEMPLATE_TYPE_LABELS.twilio, value: 'twilio' },
  { label: TEMPLATE_TYPE_LABELS.slack, value: 'slack' },
  { label: TEMPLATE_TYPE_LABELS.webhook, value: 'webhook' },
  { label: TEMPLATE_TYPE_LABELS.whatsapp, value: 'whatsapp' },
  { label: TEMPLATE_TYPE_LABELS.inbox, value: 'inbox' },
  { label: TEMPLATE_TYPE_LABELS.line, value: 'line' },
  { label: TEMPLATE_TYPE_LABELS.urban_airship, value: 'urban_airship' },
] as const satisfies {
  label: (typeof TEMPLATE_TYPE_LABELS)[Exclude<keyof typeof TEMPLATE_TYPE_LABELS, 'unset'>];
  value: Exclude<TemplateType, 'unset'>;
}[];

export const CAMPAIGN_TYPE_ICONS = {
  seg_attr: 'segment',
  transactional: 'trigger-event',
  behavioral: 'segment',
  triggered_broadcast: 'api-triggered-broadcast',
  date: 'trigger-date',
  form: 'form',
  webhook: 'webhook',
  object: 'object',
  relationship: 'connections',
  none: 'campaigns',
} as const satisfies Record<CampaignType, IconName>;
